// src/pages/league-dashboard/MyRoster.js - REFACTORED TO USE DYNASTYTABLE WITH CANONICAL STRUCTURE

import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { DynastyTable } from '../../services/tableService';
import { computeTotals } from '../../services/tables/statTotals';
import { useNavigate } from 'react-router-dom';
import {
  Calendar, Trophy,
  AlertCircle, Clock, RefreshCw, UserMinus, ChevronDown,
  ExternalLink, ArrowRightLeft, Eye,
  ChevronUp, Check,
  UserPlus, ArrowUpDown, ChevronRight, Move, Trash2
} from 'lucide-react';
import { dynastyTheme } from '../../services/colorService';
import { leaguesAPI } from '../../services/apiService';
import { formatApiError } from '../../services/errorUtils';
import { useCommissioner } from '../../contexts/CommissionerContext';
import { usePlayerModal } from '../../contexts/PlayerModalContext';
import CommissionerToggle from '../../components/commissioner/CommissionerToggle';
import TeamPageHeader from '../../components/common/TeamPageHeader';
import PromotionConfirmModal from '../../components/common/PromotionConfirmModal';
import CommissionerModeBar from '../../components/commissioner/CommissionerModeBar';
import PositionAssignmentDropdown from '../../components/league-dashboard/PositionAssignmentDropdown';
import { requiresPromotionConfirmation, computePromotionPreview } from '../../utils/rosterMoveGuards';
import { 
  getStatConfigs, 
  formatStatValue, 
  getStatValue,
  DEFAULT_BATTING_STATS,
  DEFAULT_PITCHING_STATS 
} from '../../utils/statMapping';
import {
  createActiveLineupColumns,
  createReservePlayersColumns
} from '../../services/tables/myRosterColumns';
import { classifyPlayerSection } from '../../utils/playerClassification';

// ============================================================
// Pure helpers — extracted as named exports per Bible §14
// frontend testing convention so unit tests can target the
// transformation/aggregation logic directly without exercising
// the full RTL render tree.
// ============================================================

/**
 * Selects scoring categories from league settings, prepending fundamental
 * counting stats not in the league's configured scoring categories.
 * Falls back to DEFAULT_BATTING_STATS / DEFAULT_PITCHING_STATS when
 * league has no scoring_categories configured.
 */
export const selectStatCategories = (league) => {
  if (league?.league_settings?.scoring_categories) {
    const leagueHitting = league.league_settings.scoring_categories.hitting || [];
    const leaguePitching = league.league_settings.scoring_categories.pitching || [];

    const hittingPrepend = ['G', 'AB', 'H'].filter(s => !leagueHitting.includes(s));
    const pitchingPrepend = ['G', 'IP', 'R_P', 'ER'].filter(s => !leaguePitching.includes(s));

    return {
      hitting: [...hittingPrepend, ...leagueHitting],
      pitching: [...pitchingPrepend, ...leaguePitching]
    };
  }

  return {
    hitting: DEFAULT_BATTING_STATS,
    pitching: DEFAULT_PITCHING_STATS
  };
};

/**
 * Selects stat configs for the active tab from a stat-categories shape.
 * Pure config selector. Delegates to getStatConfigs from statMapping.
 */
export const selectCurrentStatConfigs = (activeTab, statCategories) => {
  const statLabels = activeTab === 'hitting' ? statCategories.hitting : statCategories.pitching;
  const isPitcher = activeTab === 'pitching';
  return getStatConfigs(statLabels, isPitcher);
};

/**
 * Organizes a roster's players into structured sections by category and
 * status. Returns { hitting: {active, bench, dl, minors}, pitching: ... }.
 *
 * Active sections are slot-grid rows (one per league position slot),
 * with .player set to the assigned canonical player or null. Other
 * sections (bench/dl/minors) are flat arrays of canonical player objects.
 *
 * Section classification is canonical (Bible §4.5.9.1, classifyPlayerSection):
 * active-slot players are classified by the SLOT they occupy (P_* → pitching),
 * so a two-way player (TWP) in a pitching slot matches that slot instead of
 * vanishing; reserve players classify by primary position (TWP → hitting).
 * Players with status='active' but no assigned slotId fall through to
 * the bench section so they aren't invisible.
 */
export const organizeRosterByStatus = (rosterPlayers, positions) => {
  const organized = {
    hitting: { active: [], bench: [], dl: [], minors: [] },
    pitching: { active: [], bench: [], dl: [], minors: [] }
  };

  // Create position slot rows for active lineup (hitting).
  const batterPositionCounts = {};
  positions.hitting.forEach((pos) => {
    const count = batterPositionCounts[pos] || 0;
    organized.hitting.active.push({
      slotPosition: pos,
      slotId: `${pos}_${count}`,
      player: null,
    });
    batterPositionCounts[pos] = count + 1;
  });

  // Create position slot rows for active lineup (pitching).
  const pitcherPositionCounts = {};
  positions.pitching.forEach((pos) => {
    const count = pitcherPositionCounts[pos] || 0;
    organized.pitching.active.push({
      slotPosition: pos,
      slotId: `${pos}_${count}`,
      player: null,
    });
    pitcherPositionCounts[pos] = count + 1;
  });

  // Assign players to their slots.
  rosterPlayers.forEach((canonicalPlayer) => {
    // Canonical section classification (Bible §4.5.9.1): active-slot players
    // classify by the SLOT they occupy, so a TWP at P_6 lands in 'pitching'
    // and matches the P_6 slot instead of vanishing. Reserve players classify
    // by primary position (TWP → 'hitting').
    const category = classifyPlayerSection(canonicalPlayer);
    const status = canonicalPlayer.league_context?.roster?.status || 'bench';
    const assignedPosition = canonicalPlayer.league_context?.roster?.position || null;
    const parsedPlayer = { ...canonicalPlayer };

    if (status === 'active' && assignedPosition) {
      const slot = organized[category].active.find(s => s.slotId === assignedPosition);
      if (slot) {
        slot.player = parsedPlayer;
      } else {
        organized[category].bench.push(parsedPlayer);
      }
    } else if (status === 'active' && !assignedPosition) {
      organized[category].bench.push(parsedPlayer);
    } else if (status === 'bench') {
      organized[category].bench.push(parsedPlayer);
    } else if (status === 'dl') {
      organized[category].dl.push(parsedPlayer);
    } else if (status === 'minors') {
      organized[category].minors.push(parsedPlayer);
    }
  });

  return organized;
};

/**
 * Sums salary subtotals across active/bench/dl/minors per category
 * (hitting/pitching) plus an overall total. Section is canonical
 * (classifyPlayerSection, Bible §4.5.9.1): an active player's salary
 * attributes to the section of the slot he occupies (a TWP at a P slot
 * counts toward pitching payroll). Players with no status default to bench.
 * Players with non-canonical
 * status (anything outside [active, bench, dl, minors]) are excluded
 * from totals (matches source behavior).
 */
export const calculateSalarySubtotals = (rosterPlayers) => {
  const totals = {
    hitting: { active: 0, bench: 0, dl: 0, minors: 0, total: 0 },
    pitching: { active: 0, bench: 0, dl: 0, minors: 0, total: 0 },
    overall: 0,
  };

  rosterPlayers.forEach((player) => {
    // Salary attributes to the section the player's SLOT places him in
    // (Bible §4.5.9.1): a TWP active at a P slot counts toward pitching
    // payroll, not hitting. Fixes the Bat/Pit subtotal miscount.
    const category = classifyPlayerSection(player);
    const status = player.league_context?.roster?.status || 'bench';
    const salary = player.league_context?.financial?.contract_salary || 0;

    if (['active', 'bench', 'dl', 'minors'].includes(status)) {
      totals[category][status] += salary;
      totals[category].total += salary;
      totals.overall += salary;
    }
  });

  return totals;
};

/**
 * Splits the roster's contracts into the two summary-band buckets, reading
 * the canonical per-player field league_context.financial.contract_type
 * (present in build_roster_response — no server-side pre-aggregation exists,
 * so the count is derived here the same way the prior "expiring" tile derived
 * its figure: a filter over the already-loaded roster list, no new data path).
 *
 * Derivation per dispatch (DD-UI Desktop Roster, Part A):
 *   - Expiring · OP  = contract_type === 'OP' (bare OP only; decrements to NULL
 *     and is lost at season end). 'OP1'/'OP2'/'OPN' are NOT expiring.
 *   - Decision Needed = contract_type === '1' (final year of the standard
 *     initial deal — extend or release).
 * opSalaryOffBooks = sum of contract_salary for the OP-expiring players (what
 * comes off the books when they lapse). Missing salary contributes 0.
 */
export const calculateContractBuckets = (rosterPlayers) => {
  let opCount = 0;
  let opSalaryOffBooks = 0;
  let decisionCount = 0;

  rosterPlayers.forEach((player) => {
    const contractType = player.league_context?.financial?.contract_type;
    if (contractType === 'OP') {
      opCount += 1;
      opSalaryOffBooks += player.league_context?.financial?.contract_salary || 0;
    } else if (contractType === '1') {
      decisionCount += 1;
    }
  });

  return { opCount, opSalaryOffBooks, decisionCount };
};

const MyRoster = ({ leagueId, league, user, onPlayerDropped, initialViewTeamId, initialViewTeamName, bannerConfig, userTeam, highlightPlayerId = null }) => {
  const navigate = useNavigate();
  const { openPlayerModal } = usePlayerModal();
  const {
    isCommissionerMode,
    activeTeamName,
    activeTeamId,
    getTargetTeamId,
    isCommissioner
  } = useCommissioner();

  // ========================================
  // STATE MANAGEMENT
  // ========================================
  const [rosterData, setRosterData] = useState({
    team_id: null,
    team_name: 'My Team',
    players: [],
    total_salary: 0.0,
    roster_spots: { active: 0, bench: 0, injured: 0, minors: 0, total: 0 }
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [activeTab, setActiveTab] = useState('hitting');
  const [expandedSection, setExpandedSection] = useState('active');

  // Notification deep-link (Bible § 4.5.12.2): once the roster loads, scroll to + highlight
  // the player row named by ?playerId. DynastyTable emits id="roster-player-{mlbId}" per row
  // (via the rowId accessor below). No-op when absent — no wrong-row fallback. Matches the
  // trades (#trade-{id}) / waivers (#claim-{id}) scroll behavior.
  useEffect(() => {
    if (!highlightPlayerId || !rosterData?.players?.length) return;
    const el = document.getElementById(`roster-player-${highlightPlayerId}`);
    if (el) el.scrollIntoView({ behavior: 'smooth', block: 'center' });
  }, [highlightPlayerId, rosterData?.players]);

  // Waivers tab state
  const [waiverClaims, setWaiverClaims] = useState(null);   // { claims, team_priority }
  const [waiverClaimsLoading, setWaiverClaimsLoading] = useState(false);
  const [waiverClaimsError, setWaiverClaimsError] = useState('');
  
  // Position assignment modal state
  const [showPositionModal, setShowPositionModal] = useState(false);
  const [selectedPlayerForMove, setSelectedPlayerForMove] = useState(null);
  const [movePlayerCurrentStatus, setMovePlayerCurrentStatus] = useState(null);

  // 9f.3 — promotion-confirmation modal for minors-exit of price-less players.
  // pendingPromotion: { player, moveData, onConfirmed } captured at gate time; null when idle.
  const [pendingPromotion, setPendingPromotion] = useState(null);
  
  // Team browsing state
  const [allTeams, setAllTeams] = useState([]);
  const [selectedTeamId, setSelectedTeamId] = useState(null);
  const [userTeamId, setUserTeamId] = useState(null);
  const [teamsLoading, setTeamsLoading] = useState(false);
  const [viewedTeamInfo, setViewedTeamInfo] = useState(null);


  // ========================================
  // DYNAMIC LEAGUE POSITIONS
  // ========================================
  const getLeaguePositions = () => {
    // 🔍 DEBUG: Check what league object contains
    console.log('🔍 getLeaguePositions - league object:', {
      has_position_requirements: !!league?.roster_settings?.position_requirements,
      has_roster_settings: !!league?.roster_settings,
      position_requirements: league?.roster_settings?.position_requirements,
      roster_settings: league?.roster_settings,
      bench_slots: league?.roster_settings?.bench_slots,
      dl_slots: league?.roster_settings?.dl_slots,
      minor_league_slots: league?.roster_settings?.minor_league_slots
    });
    
    const positions = {
      hitting: [],
      pitching: [],
      bench: 5,
      dl: 0,
      minors: 0
    };

    if (typeof league?.roster_settings?.bench_slots === 'number') {
      positions.bench = league.roster_settings.bench_slots;
    }
    if (typeof league?.roster_settings?.dl_slots === 'number') {
      positions.dl = league.roster_settings.dl_slots;
    }
    if (typeof league?.roster_settings?.minor_league_slots === 'number') {
      positions.minors = league.roster_settings.minor_league_slots;
    }

    if (league?.roster_settings?.position_requirements && typeof league.roster_settings.position_requirements === 'object') {
      const posReqs = league.roster_settings.position_requirements;
      
      Object.entries(posReqs).forEach(([positionKey, config]) => {
        const slots = config?.slots || 0;
        
        if (positionKey === 'P' && slots > 0) {
          positions.pitching.push(...Array(slots).fill('P'));
        } else if (slots > 0) {
          positions.hitting.push(...Array(slots).fill(positionKey));
        }
      });
    } else {
      // Fallback defaults
      console.log('⚠️ WARNING: Using fallback position defaults!');
      positions.hitting = ['C', 'C', '1B', '2B', 'SS', '3B', 'MI', 'CI', 'OF', 'OF', 'OF', 'OF'];
      positions.pitching = Array(10).fill('P');
    }
    
    console.log('🔍 Final positions created:', positions);
    return positions;
  };

  const positions = getLeaguePositions();

  // ========================================
  // DYNAMIC STAT CATEGORIES FROM LEAGUE
  // ========================================
  const statCategories = useMemo(() => selectStatCategories(league), [league]);

  // Get stat configs for current tab
  const currentStatConfigs = useMemo(
    () => selectCurrentStatConfigs(activeTab, statCategories),
    [activeTab, statCategories]
  );

  // ========================================
  // HELPER FUNCTIONS
  // ========================================
  const isViewingOwnTeam = () => {
    if (isCommissionerMode) return true;
    return selectedTeamId === userTeamId || selectedTeamId === null;
  };

  const getCurrentTeamName = () => {
    // 🚨 CRITICAL: Don't trust rosterData.team_name from backend - it's often wrong!
    // ALWAYS look up the actual team name from allTeams using team_id
    
    console.log('🔍 getCurrentTeamName called:', {
      isCommissionerMode,
      activeTeamId,
      'rosterData.team_id': rosterData.team_id,
      'rosterData.team_name (DONT TRUST)': rosterData.team_name,
      userTeamId,
      selectedTeamId,
      'allTeams.length': allTeams.length
    });
    
    // Determine which team_id we're looking for
    let targetTeamId = null;
    
    if (isCommissionerMode && activeTeamId) {
      targetTeamId = activeTeamId;
      console.log('📋 Commissioner mode - looking for activeTeamId:', targetTeamId);
    } else if (selectedTeamId) {
      targetTeamId = selectedTeamId;
      console.log('📋 Selected team - looking for selectedTeamId:', targetTeamId);
    } else if (rosterData.team_id) {
      targetTeamId = rosterData.team_id;
      console.log('📋 Using rosterData.team_id:', targetTeamId);
    } else if (userTeamId) {
      targetTeamId = userTeamId;
      console.log('📋 Using userTeamId:', targetTeamId);
    }
    
    // Look up the actual team name from allTeams
    if (targetTeamId && allTeams.length > 0) {
      const team = allTeams.find(t => t.team_id === targetTeamId);
      if (team?.team_name) {
        console.log('✅ Found team name from allTeams:', team.team_name);
        return team.team_name;
      }
    }
    
    // Final fallback
    console.warn('⚠️ Could not find team name - using fallback');
    return 'Your Team';
  };

  const handleTeamChange = (e) => {
    const newTeamId = e.target.value;
    setSelectedTeamId(newTeamId);
    loadRoster(newTeamId);
  };

  // ========================================
  // ORGANIZE ROSTER DATA FOR DYNASTYTABLE
  // ========================================
  const organizeRosterForTable = useMemo(
    () => organizeRosterByStatus(rosterData.players, positions),
    [rosterData.players, positions]
  );

  // Calculate salary subtotals
  const salarySubtotals = useMemo(
    () => calculateSalarySubtotals(rosterData.players),
    [rosterData.players]
  );

  // Summary-band contract buckets (Expiring·OP / Decision Needed) — canonical
  // contract_type filter over the loaded roster, no new data path.
  const contractBuckets = useMemo(
    () => calculateContractBuckets(rosterData.players),
    [rosterData.players]
  );

  // Calculate cap info — FAAB comes from backend ledger, not client-side computation
  const calculateCapInfo = () => {
    const useDualCap = league?.salary_settings?.use_dual_cap ?? true;
    const draftCap = league?.salary_settings?.draft_cap || 600;
    const seasonCap = league?.salary_settings?.season_cap || 200;
    const totalCap = league?.salary_settings?.salary_cap || 800;

    const currentSpend = salarySubtotals.overall;

    // available_cash comes from the backend ledger (single source of truth,
    // Bible §4.5.6.1). DD-CASH-0707: the old client-side formula fallback
    // (draftCap − spend + seasonCap) masked backend failures with a DIFFERENT
    // number — Rule 9. A missing value now renders as an explicit error
    // state, never a fabricated dollar figure.
    const backendAvailable = rosterData?.available_cash;

    if (useDualCap) {
      const faab = backendAvailable != null ? backendAvailable : null;

      return {
        draftCap,
        seasonCap,
        currentSpend,
        faab,
        // Denominator for the "of $Z" subline. No FAAB-max field exists in
        // build_roster_response, so this is the configured total budget pool
        // (draft + season cap) — guarantees faab <= faabTotal, sourced from
        // league settings, never hardcoded. See report caveat.
        faabTotal: draftCap + seasonCap,
        isDualCap: true
      };
    } else {
      const remaining = backendAvailable != null ? backendAvailable : null;

      return {
        totalCap,
        currentSpend,
        remaining,
        faabTotal: totalCap,
        isDualCap: false
      };
    }
  };

  const capInfo = calculateCapInfo();

  // ========================================
  // DATA LOADING
  // ========================================
  const loadAllTeams = async () => {
    try {
      setTeamsLoading(true);
      const response = await leaguesAPI.getLeagueTeams(leagueId);
      
      if (response.success && response.teams) {
        const teamList = response.teams.map(team => ({
          team_id: team.team_id,
          team_name: team.team_name || 'Unnamed Team',
          manager_name: team.manager_name || 'Unknown Manager',
          total_players: team.total_players || 0,
          salary_used: team.salary_used || 0,
          is_user_team: team.is_user_team || false,
          team_logo_url: team.team_logo_url || null,
          team_colors: team.team_colors || null,
          team_motto: team.team_motto || null
        }));

        setAllTeams(teamList);
        
        const userTeam = teamList.find(t => t.is_user_team);
        if (userTeam) {
          setUserTeamId(userTeam.team_id);
          // 🔧 FIX: Don't auto-select user team if we're navigating to view another team
          if (!selectedTeamId && !isCommissionerMode && !initialViewTeamId) {
            setSelectedTeamId(userTeam.team_id);
          }
        }
      }
    } catch (err) {
      console.error('Error loading teams:', err);
    } finally {
      setTeamsLoading(false);
    }
  };

  const loadRoster = async (teamId = null) => {
    setLoading(true);
    setError('');

    try {
      let response;
      
      if (isCommissionerMode) {
        const commissionerTeamId = activeTeamId;
        if (!commissionerTeamId) {
          console.log('⚠️ No activeTeamId in commissioner mode!');
          setLoading(false);
          return;
        }
        
        // 🔴 DEBUG: Commissioner Mode Roster Loading
        console.log('🔴 COMMISSIONER ROSTER LOAD:', {
          timestamp: new Date().toISOString(),
          mode: 'commissioner',
          activeTeamId: activeTeamId,
          activeTeamName: activeTeamName,
          commissionerTeamId: commissionerTeamId,
          userTeamId: userTeamId,
          selectedTeamId: selectedTeamId
        });
        response = await leaguesAPI.getTeamRosterCanonical(leagueId, commissionerTeamId);
      } else {
        const targetTeamId = teamId || selectedTeamId;
        console.log('👤 Regular Mode Roster Load:', {
          targetTeamId: targetTeamId,
          userTeamId: userTeamId,
          willLoadOtherTeam: targetTeamId && targetTeamId !== userTeamId
        });
        if (targetTeamId && targetTeamId !== userTeamId) {
          // Use canonical API for viewing other teams
          response = await leaguesAPI.getTeamRosterCanonical(leagueId, targetTeamId);
        } else {
          // Use canonical API for own roster
          response = await leaguesAPI.getMyRosterCanonical(leagueId);
        }
      }

      if (response && response.success) {
        console.log('🔴🔴🔴 ROSTER API RESPONSE:', {
          player_count: response.players?.length,
          first_5_players: response.players?.slice(0, 5).map(p => p.info?.full_name),
          league_status: league?.league_status,
          current_season: league?.current_season
        });
        console.log('✅ Roster loaded successfully:', {
          numPlayers: response.players?.length,
          teamId: response.team_id,
          teamName: response.team_name,
          firstPlayer: response.players?.[0]?.info?.full_name
        });
        
        // Check if any player appears multiple times
        const playerIds = response.players?.map(p => p.ids?.mlb) || [];
        const duplicates = playerIds.filter((id, index) => playerIds.indexOf(id) !== index);
        if (duplicates.length > 0) {
          console.error('🚨 DUPLICATE PLAYERS IN RESPONSE:', duplicates);
        }
        
        setRosterData(response);
      } else {
        console.error('❌ Failed to load roster:', response?.message);
        setError(response?.message || 'Failed to load roster');
      }
    } catch (err) {
      console.error('Error loading roster:', err);
      setError('Failed to load roster');
    } finally {
      setLoading(false);
    }
  };

  // ========================================
  // WAIVER CLAIMS LOADING
  // ========================================
  const loadWaiverClaims = useCallback(async () => {
    if (!leagueId) return;
    setWaiverClaimsLoading(true);
    setWaiverClaimsError('');
    try {
      const res = await leaguesAPI.getMyWaiverClaims(leagueId);
      // getMyWaiverClaims returns raw axios response — data at .data
      setWaiverClaims(res?.data || null);
    } catch (err) {
      setWaiverClaimsError('Failed to load waiver claims.');
      console.error('Waiver claims load error:', err);
    } finally {
      setWaiverClaimsLoading(false);
    }
  }, [leagueId]);

  // ========================================
  // ACTION HANDLERS
  // ========================================
  // Open modal for moving player - wrapped in useCallback to prevent state batching issues
  const openMovePlayerModal = useCallback((player) => {
    console.log('🔍 openMovePlayerModal called:', {
      playerName: `${player.info?.first_name} ${player.info?.last_name}`,
      currentStatus: player.league_context?.roster?.status,
      currentPosition: player.league_context?.roster?.position
    });

    const currentStatus = player.league_context?.roster?.status || 'active';
    const currentPosition = player.league_context?.roster?.position;
    
    setSelectedPlayerForMove(player);
    setMovePlayerCurrentStatus(currentStatus);
    setShowPositionModal(true);
  }, []);

  // 9f.3 — single dispatch helper used by both handleMovePlayer and handlePositionAssignment.
  // Performs the API call + post-success bookkeeping. Promotion-confirmation gating happens
  // upstream in the move handlers themselves.
  const dispatchRosterMove = async ({ playerName, moveData, onSuccess }) => {
    try {
      const response = await leaguesAPI.moveRosterPlayer(leagueId, moveData);
      if (response.success) {
        setSuccessMessage(`${playerName} moved to ${moveData.new_status}${moveData.start_contract ? ' (contract started)' : ''}`);
        loadRoster();
        window.dispatchEvent(new Event('roster-updated'));
        setTimeout(() => setSuccessMessage(''), 3000);
        if (onSuccess) onSuccess();
      } else {
        setError(response.message || 'Failed to move player');
      }
    } catch (err) {
      // DD-DL-DEADLOCK-0726-FE: SURFACE THE SERVER'S MESSAGE. This catch used to discard it and
      // show a bare 'Failed to move player'. The server had already returned 422 with, verbatim:
      //   "Roster locked by the DL residency rule: Vinnie Pasquantino must be activated, dropped,
      //    or traded before this team can make other roster moves (lineup change blocked)."
      // A real owner hit this four times on 2026-07-26 and saw none of it — which is why the
      // deadlock read as an unexplained wall. formatApiError already understands every backend
      // envelope (Rule 11); use it.
      console.error('Error moving player:', err);
      setError(formatApiError(err, 'Failed to move player'));
    }
  };

  const handleMovePlayer = async (player, fromStatus, toStatus, targetSlot = null) => {
    if (!isViewingOwnTeam()) {
      setError('You can only manage your own team');
      return;
    }

    const playerId = player.ids?.league_player;
    const playerName = `${player.info?.first_name} ${player.info?.last_name}`;

    const moveData = {
      league_player_id: playerId,
      new_status: toStatus,
      target_position: targetSlot,
      reason: `Moved from ${fromStatus} to ${toStatus}`,
      start_contract: fromStatus === 'minors' && toStatus !== 'minors'
    };

    if (isCommissionerMode) {
      moveData.commissioner_action = true;
      moveData.target_team_id = getTargetTeamId(null);
    }

    // 9f.3 — gate minors-exit promotions of price-less players behind a confirmation modal.
    // Backend (9f.1) will charge regardless; this surfaces the contract event to the user.
    if (requiresPromotionConfirmation({ player, fromStatus, toStatus })) {
      setPendingPromotion({ player, playerName, moveData });
      return;
    }

    await dispatchRosterMove({ playerName, moveData });
  };

  // Handle modal submission for position assignment
  const handlePositionAssignment = async (assignmentData) => {
    const { player, roster_status, roster_position, displace_league_player_id } = assignmentData;

    const playerId = player.ids?.league_player;
    const playerName = `${player.info?.first_name} ${player.info?.last_name}`;
    const fromStatus = movePlayerCurrentStatus || 'active';

    const moveData = {
      league_player_id: playerId,
      new_status: roster_status,
      target_position: roster_position,
      reason: `Moved from ${fromStatus} to ${roster_status}`,
      start_contract: fromStatus === 'minors' && roster_status !== 'minors'
    };

    // DD-DL-DEADLOCK-0726: activate-when-full. The chooser supplies the teammate to bench; the
    // SERVER composes the atomic [bench displaced, activate flagged] plan so the §4.5.15.1 gate
    // sees both players. We only name who to displace — we never author plan steps.
    if (displace_league_player_id) {
      moveData.displace_league_player_id = displace_league_player_id;
    }

    if (isCommissionerMode) {
      moveData.commissioner_action = true;
      moveData.target_team_id = getTargetTeamId(null);
    }

    const closePositionModal = () => {
      setShowPositionModal(false);
      setSelectedPlayerForMove(null);
      setMovePlayerCurrentStatus(null);
    };

    // 9f.3 — gate minors-exit promotions of price-less players behind confirmation modal.
    // Backend (9f.1) will charge regardless; this surfaces the contract event to the user.
    if (requiresPromotionConfirmation({ player, fromStatus, toStatus: roster_status })) {
      setPendingPromotion({
        player,
        playerName,
        moveData,
        onAfterDispatch: closePositionModal,
      });
      return;
    }

    await dispatchRosterMove({ playerName, moveData, onSuccess: closePositionModal });
  };

  // 9f.3 — confirm/cancel handlers for the promotion modal.
  const handlePromotionConfirm = async () => {
    if (!pendingPromotion) return;
    const { playerName, moveData, onAfterDispatch } = pendingPromotion;
    setPendingPromotion(null);
    await dispatchRosterMove({ playerName, moveData, onSuccess: onAfterDispatch });
  };

  const handlePromotionCancel = () => {
    setPendingPromotion(null);
  };

  // Handler for clicking Move button - ALWAYS opens modal (never moves directly)
  const handleMovePlayerClick = useCallback((player) => {
    openMovePlayerModal(player);
  }, [openMovePlayerModal]);

  const handleDropPlayer = async (player) => {
    if (!isViewingOwnTeam()) {
      setError('You can only drop players from your own team');
      return;
    }

    const playerName = `${player.info?.first_name} ${player.info?.last_name}`;
    const playerId = player.ids?.league_player;

    if (!playerId) {
      setError('Error: Player ID not found. Please refresh and try again.');
      return;
    }

    // DD-WAIVER-DROPGUARD-0725 Unit A: if this player is the designated drop on a pending waiver claim,
    // cutting him voids that claim (the incident this guards). Warn BEFORE the cut so the owner decides
    // knowingly. Read-only pre-check; a failed lookup must never block the cut (non-blocking).
    let confirmMsg = `Drop ${playerName}?`;
    try {
      const impact = await leaguesAPI.getDropImpact(leagueId, playerId);
      if (impact?.waiver_claim_warning) {
        confirmMsg = impact.waiver_claim_warning;
      }
    } catch (e) {
      // ignore — the warning is best-effort; never prevent a legitimate drop on a lookup failure
    }

    if (window.confirm(confirmMsg)) {
      try {
        const dropOptions = {};
        if (isCommissionerMode) {
          dropOptions.commissioner_action = true;
          dropOptions.target_team_id = getTargetTeamId(null);
        }

        const response = await leaguesAPI.dropPlayerFromTeam(leagueId, playerId, dropOptions);

        if (response.success) {
          setSuccessMessage(`${playerName} dropped!`);
          loadRoster();
          window.dispatchEvent(new Event('roster-updated'));
          setTimeout(() => setSuccessMessage(''), 3000);
        } else {
          setError(response.message || 'Failed to drop player');
        }
      } catch (err) {
        console.error('Error dropping player:', err);
        setError(formatApiError(err, 'Failed to drop player'));
      }
    }
  };

  const handlePlayerClick = (player) => {
    const playerId = player.ids?.mlb;
    openPlayerModal(playerId, player);
  };

  const handleTradePlayer = (player) => {
    // Navigate to Trade Center with the player pre-selected
    navigate(`/league/${leagueId}`, {
      state: {
        activeSection: 'trade-center',
        initialTradePlayer: {
          player: player,
          isMyPlayer: true,
          teamId: rosterData.team_id || userTeamId
        }
      }
    });
  };

  const handleCancelWaiverClaim = async (claimId) => {
    if (!claimId) return;
    try {
      const res = await leaguesAPI.cancelWaiverClaim(leagueId, claimId);
      const data = res?.data || res;
      if (data?.success) {
        setSuccessMessage('Waiver claim cancelled.');
        setTimeout(() => setSuccessMessage(''), 3000);
        await loadWaiverClaims();
      } else {
        setError(data?.message || data?.error || 'Failed to cancel waiver claim.');
      }
    } catch (err) {
      const msg =
        err.response?.data?.message ||
        err.response?.data?.error ||
        err.message ||
        'Failed to cancel waiver claim.';
      setError(msg);
    }
  };

  // ========================================
  // EFFECTS
  // ========================================
  useEffect(() => {
    if (leagueId) loadAllTeams();
  }, [leagueId]);
  
  // Handle initial team selection from navigation
  useEffect(() => {
    if (initialViewTeamId) {
      setSelectedTeamId(initialViewTeamId);
    }
  }, [initialViewTeamId]);

  useEffect(() => {
    if (isCommissionerMode && activeTeamId) {
      // Clear stale data first to prevent showing previous team's roster
      setRosterData({
        team_id: null,
        team_name: 'Loading...',
        players: [],
        total_salary: 0.0,
        roster_spots: { active: 0, bench: 0, injured: 0, minors: 0, total: 0 }
      });
      loadRoster();
    } else if (!isCommissionerMode && (selectedTeamId || userTeamId)) {
      loadRoster(selectedTeamId || userTeamId);
    }
  }, [selectedTeamId, isCommissionerMode, activeTeamId, userTeamId]);

  // Listen for roster updates from modal actions
  useEffect(() => {
    const handleRosterUpdate = () => {
      loadRoster();
    };

    window.addEventListener('roster-updated', handleRosterUpdate);
    return () => window.removeEventListener('roster-updated', handleRosterUpdate);
  }, []);

  // Load waiver claims when waivers tab is activated
  useEffect(() => {
    if (activeTab === 'waivers') {
      loadWaiverClaims();
    }
  }, [activeTab, loadWaiverClaims]);

  // ========================================
  // RENDER ROSTER SECTION
  // ========================================
  const renderRosterSection = (title, sectionType) => {
    const isExpanded = expandedSection === sectionType;
    
    const category = activeTab;
    let rawSectionData = [];
    let sectionSalary = 0;
    let maxSlots = 0;
    
    // Check if league has bench/DL/minors
    const hasBenchSlots = positions.bench > 0;
    
    if (sectionType === 'active') {
      // Pass raw canonical players - NO TRANSFORMS
      rawSectionData = rosterData.players.filter(p => {
        // Slot-based section classification (Bible §4.5.9.1) so an active TWP
        // shows under the section of the slot he occupies.
        const category = classifyPlayerSection(p);
        const status = p.league_context?.roster?.status;
        if (activeTab === 'hitting') return category === 'hitting' && status === 'active';
        if (activeTab === 'pitching') return category === 'pitching' && status === 'active';
        return false;
      });
      maxSlots = positions[activeTab].length;
      sectionSalary = salarySubtotals[activeTab].active;
    } else {
      rawSectionData = organizeRosterForTable[category][sectionType];
      sectionSalary = salarySubtotals[activeTab][sectionType];
      
      if (sectionType === 'bench') maxSlots = positions.bench;
      else if (sectionType === 'dl') maxSlots = positions.dl;
      else if (sectionType === 'minors') maxSlots = positions.minors;
      
      // Always show bench, conditionally show DL/Minors if slots > 0
      if (sectionType !== 'bench' && maxSlots === 0) return null;
    }
    
    // Create columns based on section type (NO TRANSFORMS - pass raw data to DynastyTable)
    let columns;
    
    if (sectionType === 'active') {
      // Active lineup columns
      columns = createActiveLineupColumns({
        statConfigs: currentStatConfigs,
        onPlayerClick: (player) => {
          const playerId = player.ids?.mlb;
          openPlayerModal(playerId, player);
        },
        onMovePlayer: handleMovePlayerClick,
        onDropPlayer: handleDropPlayer,
        onTradePlayer: handleTradePlayer,
        isViewingOwnTeam: isViewingOwnTeam(),
        isPitcher: activeTab === 'pitching',
        leagueStatus: league?.league_status
      });
    } else {
      // Reserve sections columns
      columns = createReservePlayersColumns({
        statConfigs: currentStatConfigs,
        onPlayerClick: (player) => {
          const playerId = player.ids?.mlb;
          openPlayerModal(playerId, player);
        },
        onMovePlayer: handleMovePlayerClick,
        onDropPlayer: handleDropPlayer,
        onTradePlayer: handleTradePlayer,
        isViewingOwnTeam: isViewingOwnTeam(),
        sectionType: sectionType,
        isPitcher: activeTab === 'pitching',
        leagueStatus: league?.league_status
      });
    }
    
    // DD-STAT-CONSOLIDATION C3: native sticky TEAM TOTALS row (season window) via the
    // SSoT engine over this section's real players. Gate at >=2 so a single-player
    // section doesn't show a redundant totals line. Absent stats → honest '-'.
    const sectionPlayers = (rawSectionData || []).filter(p => p && p.ids);
    const sectionTotalsRow = sectionPlayers.length >= 2 ? {
      isTotalsRow: true,
      __stats: {
        [activeTab]: computeTotals(sectionPlayers, { statType: activeTab, window: 'season' }),
      },
    } : null;

    return (
      <div className="mb-6" key={sectionType}>
        <button
          onClick={() => setExpandedSection(isExpanded ? null : sectionType)}
          className={`w-full flex items-center justify-between p-4 ${dynastyTheme.components.card.interactive}`}
        >
          <div className="flex items-center gap-3">
            {/* 3px primary rule (replaces the lucide icon — matches the mock) */}
            <span style={{ width: '3px', height: '15px', borderRadius: '2px', display: 'inline-block', background: dynastyTheme.tokens.colors.primary }} />
            <span style={{ fontSize: '12px', fontWeight: 600, letterSpacing: '1px', textTransform: 'uppercase', color: dynastyTheme.tokens.colors.primary }}>
              {title} · {sectionType === 'active' ? maxSlots : `${rawSectionData.length}/${maxSlots}`} slots
            </span>
          </div>
          <div className="flex items-center gap-4">
            <span style={{ fontSize: '13px', fontWeight: 900, color: dynastyTheme.tokens.colors.primary }}>
              ${Math.round(sectionSalary)}
            </span>
            {isExpanded ?
              <ChevronUp className={`w-5 h-5 ${dynastyTheme.classes.text.neutralLight}`} /> : 
              <ChevronDown className={`w-5 h-5 ${dynastyTheme.classes.text.neutralLight}`} />
            }
          </div>
        </button>

        {isExpanded && (
          <div className="mt-2">
            {rawSectionData.length > 0 ? (
              <DynastyTable
                data={rawSectionData}
                columns={columns}
                tableType={sectionType === 'active' ? 'roster-lineup' : 'player-list'}
                leaguePositions={positions}
                playerType={activeTab}
                rowsPerItem={3}
                rowTypes={['season', 'rolling', 'accrued']}
                stickyHeader={true}
                showTotals={!!sectionTotalsRow}
                totalsRow={sectionTotalsRow}
                enableHorizontalScroll={true}
                enableVerticalScroll={false}
                maxHeight="none"
                minWidth="800px"
                // Notification deep-link (Bible § 4.5.12.2): id anchor + highlight for the
                // player named by ?playerId. id derives from the canonical MLB id; no
                // MLB id → no id emitted (no fabrication). Row-background tint is the
                // table-row equivalent of the card ring trades/waivers use (ring-2
                // doesn't render reliably across a 3-row <tr> item).
                rowId={(p) => p?.ids?.mlb ? `roster-player-${p.ids.mlb}` : null}
                getRowClassName={(p) =>
                  (highlightPlayerId && String(p?.ids?.mlb) === String(highlightPlayerId))
                    ? 'bg-yellow-400/10'
                    : ''
                }
              />
            ) : (
              <div className={`${dynastyTheme.components.card.base} py-8 text-center ${dynastyTheme.classes.text.neutralDark}`}>
                No {activeTab} in {title.toLowerCase()} (0/{maxSlots} slots used)
              </div>
            )}
            
            {sectionType !== 'active' && maxSlots > 0 && rawSectionData.length < maxSlots && (
              <div className={`${dynastyTheme.components.card.base} py-4 text-center ${dynastyTheme.classes.text.neutralDark} ${dynastyTheme.classes.border.neutral} border-t`}>
                {maxSlots - rawSectionData.length} empty {title.toLowerCase()} slots available
              </div>
            )}
          </div>
        )}
      </div>
    );
  };

  // ========================================
  // TRACK VIEWED TEAM INFO
  // ========================================
  useEffect(() => {
    if (allTeams.length > 0) {
      let targetTeamId = null;
      if (isCommissionerMode && activeTeamId) {
        targetTeamId = activeTeamId;
      } else if (selectedTeamId) {
        targetTeamId = selectedTeamId;
      } else if (userTeamId) {
        targetTeamId = userTeamId;
      }

      if (targetTeamId) {
        const teamInfo = allTeams.find(t => t.team_id === targetTeamId);
        if (teamInfo) {
          setViewedTeamInfo(teamInfo);
        }
      }
    }
  }, [selectedTeamId, userTeamId, activeTeamId, isCommissionerMode, allTeams]);

  // Get the current team's display info (pick ONE team object, then read fields)
  const activeTeamData = viewedTeamInfo || userTeam;
  const displayTeamName = viewedTeamInfo?.team_name || getCurrentTeamName();
  const logoUrl = activeTeamData?.team_logo_url;
  const teamColors = activeTeamData?.team_colors;
  const teamMotto = activeTeamData?.team_motto;

  // ========================================
  // MAIN RENDER
  // ========================================
  return (
    <div className="space-y-6">
      {/* Commissioner Mode Bar */}
      <CommissionerModeBar 
        league={league}
        onTeamSwitch={() => loadRoster()}
      />

      {/* Success/Error Messages */}
      {successMessage && (
        <div className={`${dynastyTheme.components.badge.success} p-4 rounded-lg border-2`}>
          <p className={dynastyTheme.classes.text.success}>✅ {successMessage}</p>
        </div>
      )}
      {error && (
        <div className={`${dynastyTheme.components.badge.error} p-4 rounded-lg border-2`}>
          <p className={dynastyTheme.classes.text.error}>❌ {error}</p>
        </div>
      )}

      <TeamPageHeader
        title={`${displayTeamName} Roster`}
        logoUrl={logoUrl}
        teamName={displayTeamName}
        teamColors={teamColors}
        teamMotto={teamMotto}
        bannerConfig={bannerConfig}
        isCommissioner={isCommissioner}
        subtitle={
          <span className={`text-sm ${dynastyTheme.classes.text.neutralLight}`}>
            {positions.hitting.length} hitters • {positions.pitching.length} pitchers • {positions.bench} bench
            {positions.dl > 0 && ` • ${positions.dl} DL`}
            {positions.minors > 0 && ` • ${positions.minors} minors`}
          </span>
        }
        actions={
          <>
            <CommissionerToggle
              leagueId={leagueId}
              userIsCommissioner={league?.role === 'commissioner' || isCommissioner}
            />
            <button
              onClick={() => loadRoster()}
              disabled={loading}
              className={dynastyTheme.utils.getComponent('button', 'secondary', 'md')}
            >
              <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
              Refresh
            </button>
          </>
        }
      />

      {/* Summary band — Option 1: 5 flat cells, flush row. All colors sourced
          from dynastyTheme.tokens.colors (zero hex literals). Wired to canonical
          build_roster_response-derived values; no hardcoded figures. */}
      <div
        className={`border-b ${dynastyTheme.classes.border.neutral}`}
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(5, 1fr)'
        }}
      >
        {/* Cell 1 — Total Payroll */}
        <div style={{ padding: '13px 16px' }}>
          <div style={{ fontSize: '10px', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '.4px', color: dynastyTheme.tokens.colors.muted }}>Total Payroll</div>
          <div style={{ fontSize: '24px', fontWeight: 900, lineHeight: 1.2, color: dynastyTheme.tokens.colors.textHi }}>${Math.round(capInfo.currentSpend)}</div>
          <div style={{ fontSize: '10px', color: dynastyTheme.tokens.colors.muted }}>
            Bat <span style={{ color: dynastyTheme.tokens.colors.success }}>${Math.round(salarySubtotals.hitting.total)}</span> · Pit <span style={{ color: dynastyTheme.tokens.colors.success }}>${Math.round(salarySubtotals.pitching.total)}</span>
          </div>
        </div>

        {/* Cell 2 — FAAB. DD-CASH-0707: no client-side formula fallback —
            a missing ledger balance renders as an explicit error, never a
            fabricated number (Rule 9 / §4.5.6.1). */}
        <div style={{ padding: '13px 16px', borderLeft: `1px solid ${dynastyTheme.tokens.colors.hairline}` }}>
          <div style={{ fontSize: '10px', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '.4px', color: dynastyTheme.tokens.colors.muted }}>{capInfo.isDualCap ? 'FAAB' : 'Cap Space'}</div>
          {(capInfo.isDualCap ? capInfo.faab : capInfo.remaining) != null ? (
            <>
              <div style={{ fontSize: '24px', fontWeight: 900, lineHeight: 1.2, color: dynastyTheme.tokens.colors.success }}>${Math.round(capInfo.isDualCap ? capInfo.faab : capInfo.remaining)}</div>
              <div style={{ fontSize: '10px', color: dynastyTheme.tokens.colors.muted }}>of ${Math.round(capInfo.faabTotal)}</div>
            </>
          ) : (
            <div style={{ fontSize: '12px', fontWeight: 700, marginTop: 4, color: dynastyTheme.tokens.colors.danger }}>
              Balance unavailable — ledger did not respond
            </div>
          )}
        </div>

        {/* Cell 3 — Roster config (slots) */}
        <div style={{ padding: '13px 16px', borderLeft: `1px solid ${dynastyTheme.tokens.colors.hairline}` }}>
          <div style={{ fontSize: '10px', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '.4px', color: dynastyTheme.tokens.colors.muted }}>Roster</div>
          <div style={{ fontSize: '13px', marginTop: '4px', lineHeight: 1.5, color: dynastyTheme.tokens.colors.grayCool }}>
            {positions.hitting.length + positions.pitching.length} active · {positions.bench} bench
            {(positions.dl > 0 || positions.minors > 0) && <br />}
            {positions.dl > 0 && `${positions.dl} DL`}
            {positions.dl > 0 && positions.minors > 0 && ' · '}
            {positions.minors > 0 && `${positions.minors} minors`}
          </div>
        </div>

        {/* Cell 4 — Expiring · OP */}
        <div style={{ padding: '13px 16px', borderLeft: `1px solid ${dynastyTheme.tokens.colors.hairline}`, borderTop: `2px solid ${dynastyTheme.tokens.colors.error}` }}>
          <div style={{ fontSize: '10px', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '.4px', color: dynastyTheme.tokens.colors.error }}>Expiring · OP</div>
          <div style={{ fontSize: '24px', fontWeight: 900, lineHeight: 1.2, color: dynastyTheme.tokens.colors.textHi }}>{contractBuckets.opCount}</div>
          <div style={{ fontSize: '10px', color: dynastyTheme.tokens.colors.muted }}>lost · <span style={{ color: dynastyTheme.tokens.colors.success }}>${Math.round(contractBuckets.opSalaryOffBooks)}</span> off books</div>
        </div>

        {/* Cell 5 — Decision Needed */}
        <div style={{ padding: '13px 16px', borderLeft: `1px solid ${dynastyTheme.tokens.colors.hairline}`, borderTop: `2px solid ${dynastyTheme.tokens.colors.warning}` }}>
          <div style={{ fontSize: '10px', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '.4px', color: dynastyTheme.tokens.colors.warning }}>Decision Needed</div>
          <div style={{ fontSize: '24px', fontWeight: 900, lineHeight: 1.2, color: dynastyTheme.tokens.colors.textHi }}>{contractBuckets.decisionCount}</div>
          <div style={{ fontSize: '10px', color: dynastyTheme.tokens.colors.muted }}>extend or release</div>
        </div>
      </div>

      {/* Position Tabs + Team Dropdown */}
      <div className={`flex items-center border-b ${dynastyTheme.classes.border.neutral}`} style={{ marginTop: 0 }}>
        {/* Left: Tabs */}
        <div className="flex gap-2">
          <button
            onClick={() => setActiveTab('hitting')}
            className={`px-6 py-2 font-medium ${dynastyTheme.classes.transition} ${
              activeTab === 'hitting'
                ? `${dynastyTheme.classes.text.primary} border-b-2 ${dynastyTheme.classes.border.primaryBright}`
                : `${dynastyTheme.classes.text.neutralLight} hover:text-white`
            }`}
          >
            Batters ({positions.hitting.length})
          </button>
          <button
            onClick={() => setActiveTab('pitching')}
            className={`px-6 py-2 font-medium ${dynastyTheme.classes.transition} ${
              activeTab === 'pitching'
                ? `${dynastyTheme.classes.text.primary} border-b-2 ${dynastyTheme.classes.border.primaryBright}`
                : `${dynastyTheme.classes.text.neutralLight} hover:text-white`
            }`}
          >
            Pitchers ({positions.pitching.length})
          </button>
          <button
            onClick={() => setActiveTab('waivers')}
            className={`px-6 py-2 font-medium ${dynastyTheme.classes.transition} ${
              activeTab === 'waivers'
                ? `${dynastyTheme.classes.text.primary} border-b-2 ${dynastyTheme.classes.border.primaryBright}`
                : `${dynastyTheme.classes.text.neutralLight} hover:text-white`
            }`}
          >
            Waivers
          </button>
        </div>

        {/* Right: Team Selection Dropdown */}
        {!isCommissionerMode && allTeams.length > 0 && (
          <select
            value={selectedTeamId || userTeamId || ''}
            onChange={handleTeamChange}
            className={`${dynastyTheme.components.input} ml-auto px-3 py-1.5 min-w-[200px] text-sm`}
          >
            {allTeams.map(team => {
              const suffix = team.is_user_team ? ' (Your Team)' : '';
              return (
                <option key={team.team_id} value={team.team_id}>
                  {team.team_name}{suffix}
                </option>
              );
            })}
          </select>
        )}
      </div>

      {/* Main Roster Content — only show for Batters / Pitchers tabs */}
      {activeTab !== 'waivers' && (
        <>
          <div className="space-y-4">
            {loading ? (
              <div className={`flex items-center justify-center py-12 ${dynastyTheme.components.card.base}`}>
                <RefreshCw className={`w-6 h-6 animate-spin ${dynastyTheme.classes.text.primary} mr-2`} />
                <span className={dynastyTheme.classes.text.white}>Loading roster...</span>
              </div>
            ) : (
              <>
                {renderRosterSection('Active Lineup', 'active')}
                {renderRosterSection('Bench', 'bench')}
                {positions.dl > 0 && renderRosterSection('Disabled List', 'dl')}
                {positions.minors > 0 && renderRosterSection('Minor Leagues', 'minors')}
              </>
            )}
          </div>

          {/* Empty State */}
          {!loading && rosterData.players.length === 0 && (
            <div className={`${dynastyTheme.components.card.base} p-12 text-center`}>
              <AlertCircle className={`w-12 h-12 mx-auto mb-4 ${dynastyTheme.classes.text.neutralDark}`} />
              <p className={dynastyTheme.classes.text.neutralLight}>
                No players on roster. Visit Free Agents to add players.
              </p>
            </div>
          )}
        </>
      )}

      {/* Waivers Tab Content */}
      {activeTab === 'waivers' && (
        <div className="space-y-4">
          {/* Priority banner */}
          {waiverClaims?.team_priority && (
            <div className={`${dynastyTheme.components.card.base} p-4 space-y-1`}>
              <p className={`text-sm font-medium ${dynastyTheme.classes.text.white}`}>
                Your waiver priority:{' '}
                <span className={dynastyTheme.classes.text.primary}>
                  #{waiverClaims.team_priority.priority_rank}
                </span>
              </p>
              {waiverClaims.team_priority.restricted_until &&
                new Date(waiverClaims.team_priority.restricted_until) > new Date() && (
                  <p className={`text-xs ${dynastyTheme.classes.text.neutralLight}`}>
                    Restricted until{' '}
                    {new Date(waiverClaims.team_priority.restricted_until).toLocaleString(
                      undefined,
                      { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' }
                    )}{' '}
                    (won a claim recently)
                  </p>
                )}
            </div>
          )}

          {/* Error / loading */}
          {waiverClaimsError && (
            <div className="p-4 rounded-lg bg-red-500/10 border border-red-500/30">
              <p className="text-red-400 flex items-center gap-2">
                <AlertCircle className="w-4 h-4 flex-shrink-0" />
                {waiverClaimsError}
              </p>
            </div>
          )}

          {waiverClaimsLoading ? (
            <div className={`flex items-center justify-center py-12 ${dynastyTheme.components.card.base}`}>
              <RefreshCw className={`w-6 h-6 animate-spin ${dynastyTheme.classes.text.primary} mr-2`} />
              <span className={dynastyTheme.classes.text.white}>Loading waiver claims...</span>
            </div>
          ) : (
            <div className={`${dynastyTheme.components.card.base} p-0 overflow-hidden`}>
              <div className={`px-6 py-4 border-b ${dynastyTheme.classes.border.neutral} flex items-center justify-between`}>
                <h3 className={`text-base font-semibold ${dynastyTheme.classes.text.white}`}>
                  My Active Waiver Claims
                </h3>
                <button
                  onClick={loadWaiverClaims}
                  disabled={waiverClaimsLoading}
                  className={`${dynastyTheme.utils.getComponent('button', 'secondary', 'xs')} flex items-center gap-1 disabled:opacity-50`}
                >
                  <RefreshCw className={`w-3 h-3 ${waiverClaimsLoading ? 'animate-spin' : ''}`} />
                  Refresh
                </button>
              </div>

              {(waiverClaims?.claims || []).length === 0 ? (
                <div className="py-10 text-center">
                  <p className={dynastyTheme.classes.text.neutralLight}>
                    No active waiver claims.
                  </p>
                </div>
              ) : (
                <div className="divide-y divide-neutral-800">
                  {(waiverClaims?.claims || []).map((claim) => {
                    // Backend may not return player_name yet — flag with TODO
                    // TODO: backend needs player_name in claim records
                    const playerDisplay = claim.player_name
                      ? claim.player_name
                      : claim.player_league_id
                      ? `[Player ID: ${claim.player_league_id}]`   // TODO: backend needs player_name
                      : 'Unknown Player';

                    // TODO: backend needs drop_player_name in claim records
                    const dropDisplay = claim.drop_player_name
                      ? claim.drop_player_name
                      : claim.drop_player_league_id
                      ? `[Drop ID: ${claim.drop_player_league_id}]` // TODO: backend needs drop_player_name
                      : null;

                    // Countdown to waiver_expires_at
                    let countdownDisplay = null;
                    if (claim.waiver_expires_at) {
                      const msLeft = new Date(claim.waiver_expires_at) - Date.now();
                      if (msLeft > 0) {
                        const totalMinutes = Math.floor(msLeft / 60000);
                        const days = Math.floor(totalMinutes / 1440);
                        const hrs = Math.floor((totalMinutes % 1440) / 60);
                        if (days > 0) {
                          countdownDisplay = `Processes in ${days}d ${hrs}hr`;
                        } else if (hrs > 0) {
                          countdownDisplay = `Processes in ${hrs}hr`;
                        } else {
                          countdownDisplay = `Processes in <1 hr`;
                        }
                      } else {
                        countdownDisplay = 'Processing...';
                      }
                    }

                    return (
                      <div
                        key={claim.claim_id}
                        className="flex items-center justify-between px-6 py-4 gap-4"
                      >
                        <div className="min-w-0 flex-1 space-y-0.5">
                          <p className={`text-sm font-medium ${dynastyTheme.classes.text.white}`}>
                            {playerDisplay}
                          </p>
                          {dropDisplay && (
                            <p className={`text-xs ${dynastyTheme.classes.text.neutralLight}`}>
                              Drop: {dropDisplay}
                            </p>
                          )}
                          {claim.claim_priority != null && (
                            <p className={`text-xs ${dynastyTheme.classes.text.neutralDark}`}>
                              Priority #{claim.claim_priority}
                            </p>
                          )}
                        </div>
                        <div className="flex items-center gap-4 flex-shrink-0">
                          {countdownDisplay && (
                            <div className="flex items-center gap-1 text-xs text-neutral-500">
                              <Clock className="w-3 h-3" />
                              <span>{countdownDisplay}</span>
                            </div>
                          )}
                          <button
                            onClick={() => handleCancelWaiverClaim(claim.claim_id)}
                            className={`${dynastyTheme.utils.getComponent('button', 'danger', 'xs')}`}
                          >
                            Cancel Claim
                          </button>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {/* Position Assignment Modal */}
      {showPositionModal && selectedPlayerForMove && (
        <PositionAssignmentDropdown
          player={selectedPlayerForMove}
          league={league}
          currentRoster={rosterData.players}
          onAssign={handlePositionAssignment}
          onCancel={() => {
            setShowPositionModal(false);
            setSelectedPlayerForMove(null);
            setMovePlayerCurrentStatus(null);
          }}
          isVisible={showPositionModal}
          mode="move"
          currentStatus={movePlayerCurrentStatus}
          currentPosition={selectedPlayerForMove.league_context?.roster?.position}
        />
      )}

      {/* 9f.3 — Promotion confirmation modal (minors-exit of price-less players) */}
      {pendingPromotion && (() => {
        const { cost, contractYears } = computePromotionPreview({ league });
        return (
          <PromotionConfirmModal
            isOpen
            player={pendingPromotion.player}
            playerName={pendingPromotion.playerName}
            destination={pendingPromotion.moveData.new_status}
            cost={cost}
            contractYears={contractYears}
            onConfirm={handlePromotionConfirm}
            onCancel={handlePromotionCancel}
          />
        );
      })()}
    </div>
  );
};

export default MyRoster;
